import { useCallback, useEffect, useRef, useState } from "react";
import axiosInstance from "../../../infrastructure/helpers/axiosHelper";
import { endpointsMetadata } from "../../../infrastructure/routes/endpointsMetadata";
import { getErrorToast } from "../../../infrastructure/utilities/toastUtility";
import { toLatinTransliteration } from "../../../infrastructure/utilities/transliterationUtility";
import { IExerciseSearchResultModel } from "../models/workoutPlanViewModel";

const SEARCH_DEBOUNCE_MS = 300;

interface IUseExerciseSearchArgs {
  eventId?: number;
  /**
   * Id of the plan being edited. Lets the server resolve whose history to show
   * (and which plan to exclude from it) for standalone workouts with no event.
   */
  planId?: number;
  disabled?: boolean;
  /**
   * Whether the picker is open (input focused). When open with an empty query we
   * load the full browsable list (studio + system exercises) so the user can pick
   * without having to type first.
   */
  isOpen?: boolean;
}

export const useExerciseSearch = ({ eventId, planId, disabled, isOpen }: IUseExerciseSearchArgs) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<IExerciseSearchResultModel[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestRequestIdRef = useRef(0);

  const runSearch = useCallback(
    async (q: string): Promise<IExerciseSearchResultModel[]> => {
      const requestId = ++latestRequestIdRef.current;
      try {
        const params = new URLSearchParams({ query: q, eventId: String(eventId ?? 0) });
        if (planId !== undefined) params.set("planId", String(planId));
        const res = await axiosInstance.get<IExerciseSearchResultModel[]>(
          `${endpointsMetadata.exerciseNomenclature.search}?${params.toString()}`,
        );
        if (requestId !== latestRequestIdRef.current) return [];
        return res.data ?? [];
      } catch {
        if (requestId === latestRequestIdRef.current) {
          getErrorToast("Неуспешно търсене на упражнения.");
        }
        return [];
      }
    },
    [eventId, planId],
  );

  useEffect(() => {
    if (disabled) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);

    const trimmed = query.trim();

    if (trimmed.length === 0) {
      // Closed and empty → nothing to show. Open and empty → browse the full list
      // (the server returns the studio's exercises + the seeded system catalog).
      if (!isOpen) {
        setResults([]);
        setHasSearched(false);
        return;
      }
      void (async () => {
        const fetched = await runSearch("");
        setResults(fetched);
        setHasSearched(true);
      })();
      return;
    }

    debounceRef.current = setTimeout(async () => {
      const fetched = await runSearch(trimmed);
      setResults(fetched);
      setHasSearched(true);
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, disabled, isOpen, runSearch]);

  const clear = useCallback(() => {
    setQuery("");
    setResults([]);
    setHasSearched(false);
  }, []);

  const normalizedQuery = query.trim().toLowerCase();
  const transliteratedQuery = toLatinTransliteration(normalizedQuery).toLowerCase();
  const exactMatchExists =
    normalizedQuery.length > 0 &&
    results.some((r) => {
      const name = r.name.toLowerCase();
      return (
        name === normalizedQuery ||
        toLatinTransliteration(name).toLowerCase() === transliteratedQuery
      );
    });

  return {
    query,
    setQuery,
    results,
    hasSearched,
    exactMatchExists,
    clear,
  };
};
